import React, { forwardRef, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  CompanyDomain,
  CompanyDomainFilterInputObject,
  CompanyDomainsSearchListQueryVariables,
  FilterGroupingEnum,
  useCompanyDomainsSearchListQuery,
} from 'graphql-common';
import {
  GetUrlParams,
  ORDERING_DIRECTION_ASC,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import { FloatingLabelProps } from '@lib/hocs/withFloatingLabel';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import useChipListField from '@lib/hooks/useChipListField';
import styles from './CompanyDomainChipListField.module.scss';

type Props = {
  fieldName?: string;
  ids?: string[];
  values: Values;
};

const CompanyDomainChipListField = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<unknown>) => {
    const { fieldName = '', ids, values } = props;
    const { t } = useTranslation();
    const [paginationParams, setPaginationParams] = useState<
      PaginationParams & OrderingParams
    >({
      ...PAGINATION_PARAMS,
      [GetUrlParams.OrderingField]: 'domain',
      [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_ASC,
    });

    const page = _get(paginationParams, GetUrlParams.Page);
    const perPage = _get(paginationParams, GetUrlParams.PerPage);
    const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
    const orderingDirection = _get(
      paginationParams,
      GetUrlParams.OrderingDirection,
    );

    const queryVariables: CompanyDomainsSearchListQueryVariables = {
      page: Number(page),
      limit: Number(perPage),
    };

    const filters: CompanyDomainFilterInputObject[] = [];
    if (ids?.length) {
      filters.push({
        id: {
          grouping: FilterGroupingEnum.Or,
          predicate: {
            in: ids,
          },
        },
      });
    }
    if (filters.length) {
      queryVariables.filters = filters;
    }
    if (orderingField && orderingDirection) {
      queryVariables.sorts = {
        [orderingField]: orderingDirection,
      };
    }

    const query = useCompanyDomainsSearchListQuery({
      fetchPolicy: 'no-cache',
      variables: queryVariables,
      skip: !ids?.length,
    });

    const newItems = _get(
      query.data || query.previousData,
      'data.collection',
      [],
    ) as CompanyDomain[];

    const {
      visibleItems,
      newFormFieldValue,
      handleRemove,
      handleLoadMore,
      currentPage,
      totalPages,
      firstLoading,
      error,
    } = useChipListField<CompanyDomain>({
      fieldName,
      ids,
      values,
      paginationParams,
      setPaginationParams,
      queryResult: query,
      newItems,
    });

    if (error || firstLoading) return null;

    const newFormFieldValueRender = newFormFieldValue?.length
      ? newFormFieldValue
          .filter(
            (formFieldItem) =>
              _get(formFieldItem, 'data.id') &&
              !visibleItems.some(
                (visibleItem) =>
                  _get(formFieldItem, 'data.id') === visibleItem.id,
              ),
          )
          .map((formFieldItem: SelectOption) => (
            <div key={formFieldItem.value as string}>
              <Chip
                type={ChipTypes.chipGray}
                size={ChipSizes.medium}
                label={formFieldItem.label}
                rightIconComponent={
                  <MaterialIcon
                    icon="cancel"
                    onClick={() => handleRemove(formFieldItem.value as string)}
                  />
                }
              />
            </div>
          ))
      : null;

    const visibleItemsRender = visibleItems?.length
      ? visibleItems.map((visibleItem) => (
          <div key={visibleItem.id}>
            <Chip
              type={ChipTypes.chipGray}
              size={ChipSizes.medium}
              label={visibleItem.domain}
              rightIconComponent={
                <MaterialIcon
                  icon="cancel"
                  onClick={() => handleRemove(visibleItem.id)}
                />
              }
            />
          </div>
        ))
      : null;

    if (newFormFieldValueRender === null && visibleItemsRender === null)
      return null;

    return (
      <div>
        <div className={styles.chipsList}>
          {newFormFieldValueRender}
          {visibleItemsRender}
        </div>
        {currentPage < totalPages && (
          <LinkComponent
            size={LinkSizes.small}
            type={LinkTypes.secondaryGray}
            onClick={handleLoadMore}
          >
            {t('show-more')}
          </LinkComponent>
        )}
      </div>
    );
  },
);

export default memo(CompanyDomainChipListField);
